import { omit } from 'lodash';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { AlertComponent } from './alert-component';
import { Alert, AlertContextType } from './types';

/**
 * The context used for managing and displaying alerts.
 */
export const AlertContext = createContext<AlertContextType>({
    alerts: {},
    push: (_alert: Alert) => undefined,
    remove: (_id: string) => undefined,
    removeAll: () => undefined,
});

/**
 * Custom hook for accessing the AlertContext.
 */
export const useAlerts = () => useContext(AlertContext);

export interface AlertProviderProps {
    children: ReactNode;
}

export const AlertGenericError: Alert = {
    id: `${Date.now()}`,
    message: 'Tuvimos problemas para realizar la acción',
    severity: 'failure',
};

/**
 * Provider component that manages and provides the AlertContext.
 * It allows other components to push, remove, or removeAll alerts.
 * Also, it includes the wrapper components that render the alerts.
 */
export function AlertProvider({ children }: Readonly<AlertProviderProps>) {
    const [alerts, setAlerts] = useState<Record<string, Alert>>({});

    /**
     * Adds a new alert to the alerts list.
     * @param alert The alert object to be added.
     */
    const push = useCallback(
        (alert: Alert) => {
            setAlerts((prevState) => ({
                ...prevState,
                [alert.id]: alert,
            }));
        },
        [setAlerts],
    );

    /**
     * Removes an alert from the alerts list.
     * @param id The id of the alert to be removed.
     */
    const remove = useCallback(
        (id: string) => {
            setAlerts((prevState) => {
                return omit(prevState, id);
            });
        },
        [setAlerts],
    );

    /**
     * Removes all alerts from the alerts list.
     */
    const removeAll = useCallback(() => {
        setAlerts({});
    }, [setAlerts]);

    const context = useMemo(
        () => ({
            alerts,
            push,
            remove,
            removeAll,
        }),
        [alerts, push, remove, removeAll],
    );

    return (
        <AlertContext.Provider value={context}>
            {children}
            <AlertsWrapper alerts={alerts} />
        </AlertContext.Provider>
    );
}

/**
 * Renders the alerts as AlertComponent components.
 * @param alerts The alerts to be displayed.
 */
function AlertsWrapper({ alerts }: Readonly<{ alerts: AlertContextType['alerts'] }>) {
    return (
        <div className="absolute bottom-6 left-8 flex flex-col items-start gap-2" style={{ zIndex: 500 }}>
            {Object.values(alerts).map((alert) => (
                <AlertComponent {...alert} key={alert.id} />
            ))}
        </div>
    );
}
